import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { logger } from '../utils/Logger';
import Config from '../chat/models/Config';
import PushHandler from '../chat/network/PushHandler';
import { getCookie } from '../network/cookie';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDi8BSg5aVy95EMFONvYNmhU7dIKowadbE',
  authDomain: 'myt-student-mobile.firebaseapp.com',
  databaseURL: 'https://myt-student-mobile.firebaseio.com',
  projectId: 'myt-student-mobile',
  storageBucket: 'myt-student-mobile.appspot.com',
  messagingSenderId: '401802094788',
  appId: '1:401802094788:web:56699853c1fd1aae'
};

export default class MytNotification {
  private static askedPushPermission = false;
  private static serviceWorkerRegistered = false;

  static getPermissionState = (): NotificationPermission | 'unsupported' => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }

    return Notification.permission;
  };

  private static fetchToken = async () => {
    if (window.location.hostname.includes('local')) {
      logger.debug('MytNotification: skipping token fetch on local hostname.');
      return;
    }

    if (MytNotification.serviceWorkerRegistered) {
      logger.debug('MytNotification: service worker already registered.');
      return;
    }

    // Set early to avoid races from concurrent webPushInit calls.
    MytNotification.serviceWorkerRegistered = true;

    const registerPath = '/static/js/service_worker.js';

    try {
      logger.debug('MytNotification: registering service worker for push.');
      const serviceWorkerRegistration = await navigator.serviceWorker.register(registerPath);
      logger.debug('MytNotification: service worker registered successfully.');

      const app = initializeApp(FIREBASE_CONFIG);
      const messaging = getMessaging(app);

      onMessage(messaging, (payload) => {
        logger.debug('MytNotification received. ', payload);
      });

      const currentToken = await getToken(messaging, {
        serviceWorkerRegistration: serviceWorkerRegistration
      });

      if (!!currentToken) {
        logger.debug('MytNotification: push token fetched successfully.');
        Config.shared.updatePushToken(currentToken);
      } else {
        logger.debug('MytNotification: push token unavailable.');
      }

      if (Config.shared.cached().syncFcmPushToken) {
        PushHandler.shared.registerPush(currentToken);
      }
    } catch (err) {
      MytNotification.serviceWorkerRegistered = false;
      logger.debug('MytNotification: An error occurred while retrieving token. ', err);
    }
  };

  static ensurePermission = async ({
    userGesture = false
  }: {
    userGesture?: boolean;
  } = {}): Promise<NotificationPermission | 'unsupported'> => {
    const permissionState = MytNotification.getPermissionState();

    logger.debug('MytNotification: ensurePermission called.', {
      permissionState,
      userGesture
    });

    if (permissionState === 'unsupported' || permissionState === 'denied') {
      return permissionState;
    }

    if (permissionState === 'granted') {
      await this.fetchToken();
      return permissionState;
    }

    if (!userGesture) {
      logger.debug('MytNotification: permission prompt skipped without user gesture.');
      return permissionState;
    }

    if (MytNotification.askedPushPermission) {
      logger.debug('MytNotification: permission prompt already requested in this session.');
      return MytNotification.getPermissionState();
    }

    MytNotification.askedPushPermission = true;

    const permission = await Notification.requestPermission();
    logger.debug('MytNotification: permission request resolved.', { permission });

    if (permission === 'granted') {
      await this.fetchToken();
    }

    return permission;
  };

  static webPushInit = () => {
    if (getCookie('is_impersonated') === 'true' || getCookie('impersonationFromTeacherToken')) {
      // no push token for impersonated student
      logger.debug('MytNotification: webPushInit skipped for impersonated user.');
      return;
    }

    const permissionState = MytNotification.getPermissionState();

    if (permissionState === 'unsupported') {
      logger.debug('MytNotification: browser notifications unsupported.');
      return 'This browser does not support desktop notification';
    }

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      if (MytNotification.serviceWorkerRegistered) {
        logger.debug('MytNotification: webPushInit skipped, already registered.');
        return;
      }

      isSupported().then((supported) => {
        logger.debug('MytNotification: firebase messaging support checked.', {
          supported,
          permissionState
        });

        if (supported) {
          if (permissionState === 'granted') {
            this.fetchToken();
          } else {
            logger.debug('MytNotification: push init waiting for granted permission.');
          }
        }
      });
    } else {
      logger.debug('MytNotification: push APIs unavailable in current browser.');
    }
  };

  static canShowLocalNotification = (): boolean => {
    const permissionState = MytNotification.getPermissionState();
    const canShow = permissionState === 'granted';

    if (!canShow) {
      logger.debug('MytNotification: local notification not allowed.', {
        permissionState
      });
    }

    return canShow;
  };

  static showLocalNotification = (
    notificationTag: string,
    notificationBody: string = 'You got a new message',
    onClick?: (notificationTag: string) => void
  ): boolean => {
    if (!MytNotification.canShowLocalNotification()) {
      return false;
    }

    const notificationTitle = 'MyYogaTeacher';
    const notificationOptions = {
      body: notificationBody,
      icon: '/static/images/notification_icon_256.png',
      badge: '/static/images/notification_icon_128.png',
      vibrate: [500, 110, 500, 110, 450, 110, 200, 110, 170, 40, 450, 110, 200, 110, 170, 40, 500],
      sound: '/static/sounds/notification.wav',
      tag: notificationTag
    };

    logger.debug('MytNotification: showing local notification.', {
      notificationTag
    });

    const notification = new Notification(notificationTitle, notificationOptions);
    notification.onclick = () => {
      if (!!window) {
        window.focus();
      }

      onClick?.(notificationTag);
      notification.close();
    };

    return true;
  };
}
